import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Briefcase, Clock, Users, Globe, GraduationCap, Heart, ArrowUpRight } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { useOpenRoles } from '@/lib/cms';
import type { Role } from '@/lib/cms-types';

const BENEFITS = [
  { icon: Globe, title: 'Remote-first', description: 'Work from anywhere. Quarterly retreats to reconnect.' },
  { icon: Heart, title: 'Health stipend', description: 'Monthly allowance for health, wellness, and equipment.' },
  { icon: GraduationCap, title: 'Learning budget', description: 'USD 2,000 per year for courses, books, conferences.' },
  { icon: Clock, title: 'Flexible hours', description: 'Async-first. Work when you do your best work.' },
  { icon: Users, title: 'Senior team', description: 'No juniors. No hero culture. Real ownership.' },
  { icon: Briefcase, title: 'Competitive pay', description: 'Top-quartile local + equity. Transparent bands.' },
];

const PROCESS = [
  { step: 1, title: 'Intro call', description: '30 minutes with the founder or hiring lead. No prep.' },
  { step: 2, title: 'Take-home or pairing', description: 'A small, paid exercise or live pairing — your choice.' },
  { step: 3, title: 'Team interviews', description: '3 conversations with the people you\'d work with. No whiteboard puzzles.' },
  { step: 4, title: 'Offer', description: 'Written offer within 48 hours. Reference checks only after verbal alignment.' },
];

export function CareersPage() {
  const { data: roles = [], isLoading } = useOpenRoles();

  return (
    <>
      <Meta
        title="Careers"
        description="Build the future of digital products from Africa. Join the Vonlof team."
        path="/careers"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Careers</p>
          </FadeIn>
          <TextSplit
            text="Build the future of digital products from Africa."
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
          <FadeIn delay={0.5}>
            <p className="mt-6 max-w-2xl text-lg text-fg-muted">
              We're a small, senior team that ships. We hire for taste, judgment, and the patience to do things right.
            </p>
          </FadeIn>
        </MaskReveal>

        <section className="container-wide">
          {/* CULTURE */}
          <section className="section-y-lg">
            <FadeIn>
              <h2 className="text-h2 text-fg">Our culture.</h2>
            </FadeIn>
            <FadeIn delay={0.05}>
              <p className="mt-6 max-w-3xl text-fg-muted">
                We are a small, distributed team of seniors who care about craft. We don't do hero culture. We do clear ownership, calm pace, and shipping.
                We write things down, we disagree directly, and we trust each other to do the work without surveillance.
              </p>
            </FadeIn>
          </section>

          {/* BENEFITS */}
          <section className="section-y-lg">
            <FadeIn>
              <h2 className="text-h2 text-fg">Benefits.</h2>
            </FadeIn>
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {BENEFITS.map((b) => (
                <StaggerItem key={b.title}>
                  <div className="h-full rounded-xl border border-border bg-bg-elevated/40 p-6">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-gold-500/10 text-gold-500">
                      <b.icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold tracking-tight">{b.title}</h3>
                    <p className="mt-2 text-sm text-fg-muted">{b.description}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </section>

          {/* PROCESS */}
          <section className="section-y-lg">
            <FadeIn>
              <h2 className="text-h2 text-fg">Hiring process.</h2>
            </FadeIn>
            <FadeIn delay={0.05}>
              <p className="mt-2 max-w-2xl text-fg-muted">No surprises. Four steps, fully remote.</p>
            </FadeIn>
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {PROCESS.map((p) => (
                <StaggerItem key={p.step}>
                  <div className="h-full rounded-xl border border-border bg-bg-elevated/40 p-6">
                    <span className="display-serif text-5xl text-gold-500/40">
                      {String(p.step).padStart(2, '0')}
                    </span>
                    <h3 className="mt-2 text-lg font-semibold tracking-tight">{p.title}</h3>
                    <p className="mt-2 text-sm text-fg-muted">{p.description}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </section>

          {/* OPEN ROLES */}
          <section className="section-y-lg">
            <FadeIn>
              <div className="flex items-end justify-between gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Open roles</p>
                  <h2 className="mt-3 text-h2 text-fg">Where you could join.</h2>
                </div>
                <Badge variant="gold">{isLoading ? '…' : roles.length} open</Badge>
              </div>
            </FadeIn>

            {isLoading ? (
              <div className="mt-8 grid gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : roles.length === 0 ? (
              <FadeIn delay={0.1}>
                <Card className="mt-8 p-10 text-center">
                  <p className="text-base font-semibold">No open roles right now.</p>
                  <p className="mt-2 text-sm text-fg-muted">
                    But we always want to hear from great people. Send us a note — even if there's no role listed.
                  </p>
                  <div className="mt-4">
                    <Button asChild>
                      <Link to="/contact">
                        Reach out <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </FadeIn>
            ) : (
              <Stagger className="mt-8 space-y-3">
                {roles.map((role) => (
                  <StaggerItem key={role.id}>
                    <RoleRow role={role} />
                  </StaggerItem>
                ))}
              </Stagger>
            )}
          </section>
        </section>
      </main>
    </>
  );
}

function RoleRow({ role }: { role: Role }) {
  return (
    <Link
      to={`/careers/${role.slug}`}
      className="group block rounded-xl border border-border bg-bg-elevated/40 p-6 transition-all hover:-translate-y-0.5 hover:border-gold-500/40 hover:shadow-gold"
    >
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <h3 className="text-lg font-semibold tracking-tight group-hover:text-gold-500">{role.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
            {role.department && <Badge variant="outline">{role.department}</Badge>}
            {role.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {role.location}
              </span>
            )}
            {role.type && (
              <span className="inline-flex items-center gap-1">
                <Briefcase className="h-3.5 w-3.5" /> {role.type}
              </span>
            )}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-gold-500">
          View role <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}